<template>
  <div class="card p-shadow-app">
    <h2 class="mb-2">Gerir Estados Internos</h2>

    <Toast :style="{ width: $isMobile() ? '85vw' : '' }" />

    <div class="filter-section p-mb-3">
      <label for="parentStatus">Filtrar por Estado Principal:</label>
      <Dropdown
        id="parentStatus"
        v-model="selectedParentStatus"
        :options="parentStatuses"
        optionLabel="field"
        optionValue="id"
        placeholder="Todos os Estados"
        :showClear="true"
        @change="filterByParentStatus"
        class="w-full p-ml-2"
      />
      <Button
        label="Criar Novo Estado Interno"
        icon="pi pi-plus"
        class="p-button-success p-ml-2"
        @click="openCreateDialog"
      />
    </div>

    <div v-if="groupedStates.length > 0" class="states-container">
      <div
        v-for="group in groupedStates"
        :key="group.parentStatus"
        class="status-group p-mb-4"
      >
        <div class="group-header">
          <h3>{{ group.parentStatusName }}</h3>
          <Badge :value="group.states.length" class="p-ml-2" />
        </div>

        <draggable
          v-model="group.states"
          @end="onReorder(group.parentStatus)"
          tag="ul"
          class="p-list"
          handle=".drag-handle"
        >
          <li
            v-for="(state, index) in group.states"
            :key="state.id"
            class="p-item"
            :class="{ inactive: !state.active }"
          >
            <span class="drag-handle">
              <i class="pi pi-bars"></i>
            </span>
            <span class="p-order">#{{ index + 1 }}</span>
            <span class="p-name">{{ state.name }}</span>

            <Divider layout="vertical" />

            <span class="p-d-flex p-flex-column p-ai-center">
              <label :for="'active-' + state.id">Ativo</label>
              <Checkbox
                v-model="state.active"
                :inputId="'active-' + state.id"
                :binary="true"
                :trueValue="1"
                :falseValue="0"
                class="p-mt-2"
                @change="toggleActive(state.id)"
              />
            </span>

            <Divider layout="vertical" />

            <span
              class="p-color"
              :style="{ backgroundColor: state.color }"
              @click="editColor(state)"
            >
              {{ state.color || "Sem cor" }}
            </span>

            <Divider layout="vertical" />

            <div class="actions">
              <Button
                icon="pi pi-pencil"
                @click="editState(state)"
                class="p-button-text p-button-sm p-button-info"
                v-tooltip.top="'Editar'"
              />
              <Button
                icon="pi pi-trash"
                @click="confirmDelete(state)"
                class="p-button-text p-button-sm p-button-danger"
                v-tooltip.top="'Eliminar'"
              />
              <divider layout="vertical" />
              <div class="arrows">
                <Button
                  icon="pi pi-arrow-up"
                  @click="moveUp(group.parentStatus, index)"
                  class="p-button-text p-button-sm"
                  :disabled="index === 0"
                />
                <Button
                  icon="pi pi-arrow-down"
                  @click="moveDown(group.parentStatus, index)"
                  class="p-button-text p-button-sm"
                  :disabled="index === group.states.length - 1"
                />
              </div>
            </div>
          </li>
        </draggable>
      </div>
    </div>

    <div v-else class="no-data">
      <p>Nenhum estado interno encontrado.</p>
    </div>

    <Dialog
      :header="isEditing ? 'Editar Estado Interno' : 'Criar Estado Interno'"
      :visible="stateDialogVisible"
      @hide="closeStateDialog"
      :modal="true"
      :closable="true"
      class="state-dialog"
      :style="{ width: $isMobile() ? '95vw' : '500px' }"
    >
      <div class="p-fluid">
        <div class="p-field-checkbox p-mb-3">
          <Checkbox
            id="stateActive"
            v-model="formState.active"
            :binary="true"
            :trueValue="1"
            :falseValue="0"
          />
          <label for="stateActive">Ativo</label>
        </div>

        <div class="p-field p-mb-3">
          <label for="stateName">Nome do Estado *</label>
          <InputText
            id="stateName"
            v-model="formState.name"
            :class="{ 'p-invalid': submitted && !formState.name }"
            placeholder="Ex: DESENVOLVIMENTO"
          />
          <small class="p-error" v-if="submitted && !formState.name">
            Nome é obrigatório.
          </small>
        </div>

        <div class="p-field p-mb-3">
          <label for="parentStatusDropdown">Estado Principal *</label>
          <Dropdown
            id="parentStatusDropdown"
            v-model="formState.parent_status_id"
            :options="parentStatuses"
            optionLabel="field"
            optionValue="id"
            placeholder="Selecione o estado principal"
            :class="{ 'p-invalid': submitted && !formState.parent_status_id }"
          />
          <small
            class="p-error"
            v-if="submitted && !formState.parent_status_id"
          >
            Estado principal é obrigatório.
          </small>
        </div>

        <div class="p-field p-mb-3">
          <label for="stateColor">Cor</label>
          <div class="color-input-group">
            <InputText
              id="stateColor"
              v-model="formState.color"
              placeholder="#FF5733"
              class="color-input"
            />
            <ColorPicker v-model="formState.color" format="hex" :inline="true" />
          </div>
          <div class="preview-box">
                <h3>Pré-visualizar cor:</h3>
                <div
                    class="color-preview"
                    :style="{
                    backgroundColor: formState.color.startsWith('#')
                        ? formState.color
                        : '#' + formState.color,
                    }"
                ></div>
                <div class="color-code">
                    {{
                    formState.color.startsWith("#")
                        ? formState.color
                        : "#" + formState.color
                    }}
                </div>
            </div>
        </div>
      </div>

      <template #footer>
        <Button
          label="Cancelar"
          icon="pi pi-times"
          class="p-button-text"
          @click="closeStateDialog"
        />
        <Button
          :label="isEditing ? 'Atualizar' : 'Criar'"
          icon="pi pi-check"
          @click="saveState"
          :loading="saving"
        />
      </template>
    </Dialog>

    <Dialog
      header="Alterar Cor"
      :visible="colorDialogVisible"
      @hide="colorDialogVisible = false"
      :modal="true"
      :closable="false"
      class="color-dialog"
    >
      <div class="color-container">
        <ColorPicker
          v-model="selectedColor"
          format="hex"
          :inline="true"
          class="color-picker"
        />

        <div class="preview-box">
          <h3>Pré-visualizar cor:</h3>
          <div
            class="color-preview"
            :style="{
              backgroundColor: selectedColor.startsWith('#')
                ? selectedColor
                : '#' + selectedColor,
            }"
          ></div>
          <div class="color-code">
            {{
              selectedColor.startsWith("#")
                ? selectedColor
                : "#" + selectedColor
            }}
          </div>
        </div>
      </div>

      <template #footer>
        <div class="dialog-footer">
          <Button
            label="Cancelar"
            class="p-button-text"
            @click="colorDialogVisible = false"
          />
          <Button label="Guardar" @click="saveColor" />
        </div>
      </template>
    </Dialog>

    <Dialog
      header="Confirmar Exclusão"
      :visible="deleteDialogVisible"
      @hide="deleteDialogVisible = false"
      :modal="true"
      :style="{ width: $isMobile() ? '95vw' : '450px' }"
    >
      <div class="confirmation-content">
        <i class="pi pi-exclamation-triangle p-mr-3" style="font-size: 2rem" />
        <span v-if="stateToDelete">
          Tem certeza que deseja eliminar o estado
          <b>{{ stateToDelete.name }}</b
          >?
        </span>
      </div>
      <template #footer>
        <Button
          label="Cancelar"
          icon="pi pi-times"
          class="p-button-text"
          @click="deleteDialogVisible = false"
        />
        <Button
          label="Eliminar"
          icon="pi pi-trash"
          class="p-button-danger"
          @click="deleteState"
          :loading="deleting"
        />
      </template>
    </Dialog>
  </div>
</template>

<script>
import InternalStateService from "../services/internal-state.service";
import PatService from "../services/pat.service";
import ColorPicker from "primevue/colorpicker";
import Dialog from "primevue/dialog";
import Button from "primevue/button";
import InputText from "primevue/inputtext";
import Dropdown from "primevue/dropdown";
import Checkbox from "primevue/checkbox";
import Divider from "primevue/divider";
import Badge from "primevue/badge";
import draggable from "vuedraggable";

export default {
  name: "InternalStatesManager",
  components: {
    ColorPicker,
    Dialog,
    Button,
    InputText,
    Dropdown,
    Checkbox,
    Divider,
    Badge,
    draggable,
  },
  data() {
    return {
      internalStates: [],
      parentStatuses: [],
      selectedParentStatus: null,
      groupedStates: [],

      stateDialogVisible: false,
      colorDialogVisible: false,
      deleteDialogVisible: false,

      formState: {
        id: null,
        name: "",
        parent_status_id: "",
        color: "#4ECDC4",
        active: 1,
      },
      isEditing: false,
      submitted: false,
      saving: false,
      deleting: false,

      selectedState: null,
      selectedColor: "#000000",

      stateToDelete: null,
    };
  },
  created() {
    this.fetchData();
  },
  methods: {
    async fetchData() {
      await Promise.all([this.fetchInternalStates(), this.fetchParentStatuses()]);
      this.groupStatesByParent();
    },

    async fetchInternalStates() {
      try {
        this.internalStates = await InternalStateService.getAllInternalStates();
      } catch (error) {
        this.$toast.add({
          severity: "error",
          summary: "Erro",
          detail: "Falha ao carregar estados internos.",
          life: 3000,
        });
      }
    },

    async fetchParentStatuses() {
      try {
        this.parentStatuses = await PatService.getAllStatus();
      } catch (error) {
        this.$toast.add({
          severity: "error",
          summary: "Erro",
          detail: "Falha ao carregar estados principais.",
          life: 3000,
        });
      }
    },

    groupStatesByParent() {
      const filtered = this.selectedParentStatus
        ? this.internalStates.filter(
            (s) => s.parent_status_id === this.selectedParentStatus
          )
        : this.internalStates;

      const grouped = {};
      filtered.forEach((state) => {
        if (!grouped[state.parent_status_id]) {
          grouped[state.parent_status_id] = [];
        }
        grouped[state.parent_status_id].push(state);
      });

      this.groupedStates = Object.keys(grouped)
        .map((key) => ({
          parentStatus: key,
          parentStatusName: grouped[key][0].parent_status
            ? grouped[key][0].parent_status.campo
            : "Desconhecido",
          states: grouped[key].sort((a, b) => a.order - b.order),
        }))
        .sort((a, b) => a.parentStatus.localeCompare(b.parentStatus));
    },

    filterByParentStatus() {
      this.groupStatesByParent();
    },

    openCreateDialog() {
      this.isEditing = false;
      this.formState = {
        id: null,
        name: "",
        parent_status_id: this.selectedParentStatus || "",
        color: "#4ECDC4",
        active: 1,
      };
      this.submitted = false;
      this.stateDialogVisible = true;
    },

    async moveUp(parentStatus, index) {
      const group = this.groupedStates.find((g) => g.parentStatus === parentStatus);

      if (!group || index === 0) return;

      const temp = group.states[index - 1];
      group.states.splice(index - 1, 1, group.states[index]);
      group.states.splice(index, 1, temp);

      await this.onReorder(parentStatus);
    },

    async moveDown(parentStatus, index) {
      const group = this.groupedStates.find((g) => g.parentStatus === parentStatus);

      if (!group || index === group.states.length - 1) return;

      const temp = group.states[index + 1];
      group.states.splice(index + 1, 1, group.states[index]);
      group.states.splice(index, 1, temp);

      await this.onReorder(parentStatus);
    },

    editState(state) {
      this.isEditing = true;
      this.formState = { ...state };
      this.submitted = false;
      this.stateDialogVisible = true;
    },

    closeStateDialog() {
      this.stateDialogVisible = false;
      this.formState = {
        id: null,
        name: "",
        parent_status_id: "",
        color: "#4ECDC4",
        active: 1,
      };
      this.submitted = false;
    },

    async saveState() {
      this.submitted = true;

      if (!this.formState.name || !this.formState.parent_status_id) {
        return;
      }

      this.saving = true;

      try {
        if (this.isEditing) {
          await InternalStateService.updateInternalState(
            this.formState.id,
            this.formState
          );
          this.$toast.add({
            severity: "success",
            summary: "Sucesso",
            detail: "Estado interno atualizado com sucesso.",
            life: 3000,
          });
        } else {
          await InternalStateService.createInternalState(this.formState);
          this.$toast.add({
            severity: "success",
            summary: "Sucesso",
            detail: "Estado interno criado com sucesso.",
            life: 3000,
          });
        }

        await this.fetchInternalStates();
        this.groupStatesByParent();
        this.closeStateDialog();
      } catch (error) {
        this.$toast.add({
          severity: "error",
          summary: "Erro",
          detail: "Falha ao guardar estado interno.",
          life: 3000,
        });
      } finally {
        this.saving = false;
      }
    },

    confirmDelete(state) {
      this.stateToDelete = state;
      this.deleteDialogVisible = true;
    },

    async deleteState() {
      this.deleting = true;

      try {
        await InternalStateService.deleteInternalState(this.stateToDelete.id);
        this.$toast.add({
          severity: "success",
          summary: "Sucesso",
          detail: "Estado interno eliminado com sucesso.",
          life: 3000,
        });

        await this.fetchInternalStates();
        this.groupStatesByParent();
        this.deleteDialogVisible = false;
        this.stateToDelete = null;
      } catch (error) {
        this.$toast.add({
          severity: "error",
          summary: "Erro",
          detail: "Falha ao eliminar estado interno.",
          life: 3000,
        });
      } finally {
        this.deleting = false;
      }
    },

    async onReorder(parentStatus) {
      const group = this.groupedStates.find(
        (g) => g.parentStatus === parentStatus
      );

      if (!group) return;

      group.states.forEach((state, index) => {
        state.order = index + 1;
      });

      try {
        await InternalStateService.reorderInternalStates({ states: group.states });
        this.$toast.add({
          severity: "success",
          summary: "Sucesso",
          detail: "Ordem atualizada com sucesso.",
          life: 3000,
        });
      } catch (error) {
        this.$toast.add({
          severity: "error",
          summary: "Erro",
          detail: "Falha ao atualizar ordem.",
          life: 3000,
        });
        await this.fetchInternalStates();
        this.groupStatesByParent();
      }
    },

    editColor(state) {
      this.selectedState = state;
      this.selectedColor = state.color || "#000000";
      this.colorDialogVisible = true;
    },

    async saveColor() {
      if (this.selectedState) {
        const updatedColor = this.selectedColor.startsWith("#")
          ? this.selectedColor
          : "#" + this.selectedColor;

        this.selectedState.color = updatedColor;

        try {
          await InternalStateService.updateInternalState(
            this.selectedState.id,
            { color: updatedColor }
          );
          this.$toast.add({
            severity: "success",
            summary: "Sucesso",
            detail: "Cor atualizada com sucesso.",
            life: 3000,
          });
        } catch (error) {
          this.$toast.add({
            severity: "error",
            summary: "Erro",
            detail: "Falha ao atualizar cor.",
            life: 3000,
          });
        }
      }
      this.colorDialogVisible = false;
    },

    async toggleActive(id) {
      try {
        await InternalStateService.toggleActive(id);
        this.$toast.add({
          severity: "success",
          summary: "Sucesso",
          detail: "Estado atualizado com sucesso.",
          life: 3000,
        });
      } catch (error) {
        this.$toast.add({
          severity: "error",
          summary: "Erro",
          detail: "Falha ao atualizar estado.",
          life: 3000,
        });
        await this.fetchInternalStates();
        this.groupStatesByParent();
      }
    },
  },
};
</script>

<style scoped>
.filter-section {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
  padding: 15px;
  background: #f8f9fa;
  border-radius: 8px;
}

.status-group {
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 15px;
  background: #fff;
}

.group-header {
  display: flex;
  align-items: center;
  margin-bottom: 15px;
  padding-bottom: 10px;
  border-bottom: 2px solid #e0e0e0;
}

.group-header h3 {
  margin: 0;
  font-size: 1.2rem;
  color: #333;
}

.p-list {
  list-style: none;
  padding: 0;
  margin: 0;
}

.p-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px;
  background: #f7f7f7;
  margin-bottom: 8px;
  border-radius: 6px;
  gap: 10px;
  transition: all 0.2s;
}

.p-item:hover {
  background: #ececec;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.p-item.inactive {
  opacity: 0.5;
}

.drag-handle {
  cursor: grab;
  color: #999;
  padding: 0 8px;
}

.drag-handle:active {
  cursor: grabbing;
}

.p-order {
  font-weight: bold;
  width: 40px;
  color: #666;
}

.p-name {
  flex: 1;
  font-weight: 600;
  color: #333;
}

.p-color {
  padding: 6px 16px;
  border-radius: 5px;
  cursor: pointer;
  color: #fff;
  font-weight: 500;
  min-width: 100px;
  text-align: center;
  transition: transform 0.2s;
}

.p-color:hover {
  transform: scale(1.05);
}

.actions {
  display: flex;
  gap: 5px;
}

.no-data {
  text-align: center;
  padding: 40px;
  color: #999;
}

.color-input-group {
  display: flex;
  gap: 10px;
  align-items: center;
}

.color-input {
  flex: 1;
}

.color-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 20px;
  padding: 10px 0;
}

.preview-box {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.color-preview {
  width: 80px;
  height: 80px;
  border-radius: 10px;
  border: 1px solid #ccc;
  box-shadow: 0 0 5px rgba(0, 0, 0, 0.2);
  margin-bottom: 10px;
  transition: background-color 0.3s ease;
}

.color-code {
  font-size: 16px;
  font-weight: bold;
  color: #333;
}

.confirmation-content {
  display: flex;
  align-items: center;
  padding: 20px;
}

.dialog-footer {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
}

.arrows {
  display: flex;
  flex-direction: column;
  gap: 5px;
}

@media (max-width: 768px) {
  .filter-section {
    flex-direction: column;
    align-items: stretch;
  }

  .p-item {
    flex-wrap: wrap;
  }
}
</style>